import { ArrowLeftMini, ArrowRightMini } from "@medusajs/icons"
import Link from "next/link"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"

/**
 * 死路页共用外观（404 两张 + 运行时错误页那张）。
 *
 * 形状照 shadcnui-blocks 的 not-found-06：图标徽章 → 标题 → 说明 → 2×2 卡片 → 底部回首页。
 * 这里不带 "use client"、不取数、不发请求 —— 一份代码要同时被服务端（两个 not-found）和
 * 客户端（app/[locale]/error.tsx）渲染。
 *
 * 两个约定（e2e-ego 的 106 项会按字面查）：
 *   - 最外层容器永远带 data-testid="error-shell"
 *   - 底部「回首页」永远带 data-testid="error-home"，且全页只出现一次
 */
export type ResultItem =
  | {
      kind: "link"
      icon: ReactNode
      title: string
      description: string
      href: string
    }
  | {
      kind: "action"
      icon: ReactNode
      title: string
      description: string
      onClick: () => void
      testId: string
    }

type ResultPanelProps = {
  icon: ReactNode
  title: string
  description: string
  /** 网格上方那句「也许用得上」，可不传 */
  gridLabel?: string
  items: ResultItem[]
  homeLabel: string
  /** 回首页的目标；带 locale 的页面自己拼前缀，根 404 传 "/" */
  homeHref?: string
}

const ITEM_CLASS =
  "group flex items-center gap-4 rounded-rounded border border-ui-border-base bg-ui-bg-component p-4 text-left transition-colors hover:bg-ui-bg-component-hover"

const ItemIcon = ({ children }: { children: ReactNode }) => (
  <span className="flex size-9 shrink-0 items-center justify-center rounded-rounded border border-ui-border-base bg-ui-bg-base text-ui-fg-subtle">
    {children}
  </span>
)

const ItemText = ({
  title,
  description,
}: {
  title: string
  description: string
}) => (
  <span className="min-w-0 flex-1">
    <span className="block text-small-semi text-ui-fg-base">{title}</span>
    <span className="block truncate text-xsmall-regular text-ui-fg-subtle">
      {description}
    </span>
  </span>
)

const ItemArrow = () => (
  <ArrowRightMini className="shrink-0 text-ui-fg-subtle transition-transform group-hover:translate-x-0.5" />
)

const ResultPanel = ({
  icon,
  title,
  description,
  gridLabel,
  items,
  homeLabel,
  homeHref = "/",
}: ResultPanelProps) => {
  return (
    <div
      data-testid="error-shell"
      className="flex min-h-[calc(100vh-64px)] flex-col items-center justify-center px-6 py-16 text-center"
    >
      <div className="flex size-12 items-center justify-center rounded-large border border-ui-border-base bg-ui-bg-component text-ui-fg-subtle">
        {icon}
      </div>
      <h1 className="mt-6 text-2xl-semi text-ui-fg-base">{title}</h1>
      <p className="mt-2 max-w-sm text-small-regular text-ui-fg-subtle">
        {description}
      </p>
      {gridLabel ? (
        <p className="mt-10 text-xsmall-regular text-ui-fg-subtle">
          {gridLabel}
        </p>
      ) : null}
      <div
        className={`grid w-full max-w-lg gap-3 small:grid-cols-2 ${
          gridLabel ? "mt-4" : "mt-10"
        }`}
      >
        {items.map((item) =>
          item.kind === "action" ? (
            <button
              key={item.testId}
              type="button"
              data-testid={item.testId}
              onClick={item.onClick}
              className={ITEM_CLASS}
            >
              <ItemIcon>{item.icon}</ItemIcon>
              <ItemText title={item.title} description={item.description} />
              <ItemArrow />
            </button>
          ) : (
            <Link key={item.href} href={item.href} className={ITEM_CLASS}>
              <ItemIcon>{item.icon}</ItemIcon>
              <ItemText title={item.title} description={item.description} />
              <ItemArrow />
            </Link>
          )
        )}
      </div>
      <Button asChild variant="outline" className="mt-8">
        <Link data-testid="error-home" href={homeHref}>
          <ArrowLeftMini className="size-4" />
          {homeLabel}
        </Link>
      </Button>
    </div>
  )
}

export default ResultPanel
